import { Icons } from '@icons/icons';

import { copyTextUtil } from '@utils/copy';
import { generateDurationString } from '@utils/date';

import { IActiveAlarmOran, IActiveAlarmsFlexidas, IAlarmInformation } from '@interfaces/alarms/alarm';

import { EAlarmStatus, EAlarmStatusTitle } from '@enums/alarms';
import { EVendorsValues } from '@enums/common';

import { TAlarmFieldInfo } from './AlarmDetailsField/AlarmDetailsField';

const getLimenetFields = (alarm: IAlarmInformation): TAlarmFieldInfo[] => [
	{
		label: 'Alarm ID',
		value: alarm.alarmId,
		onClick: () => copyTextUtil(alarm.alarmId),
	},
	{
		label: 'Alarm Name',
		value: alarm.name,
		onClick: () => copyTextUtil(alarm.name),
	},
	{
		label: 'Code',
		value: alarm.codeName,
	},
	{
		label: 'Status',
		value: alarm.status.name === EAlarmStatus.active
			? EAlarmStatusTitle.active
			: EAlarmStatusTitle.inactive,
	},
	{
		label: 'Severity',
		value: alarm.severity.title,
		color: alarm.severity.color,
	},
	{
		label: 'Group',
		value: alarm.groups.map((group) => group.title).join(', '),
	},
	{
		label: 'Site ID',
		value: alarm.siteId || '-',
	},
	{
		label: 'Flex-5G',
		value: alarm.crowdcellSystemId || '-',
	},
	{
		label: 'Raised',
		value: alarm.createdAtText,
	},
	{
		label: 'Updated',
		value: alarm.updatedAtText,
	},
	{
		label: 'Cleared',
		value: alarm.clearedAtText || '-',
	},
	{
		label: 'Duration',
		value: `${alarm.durationMinutes}m`,
	},
	{
		label: 'Condition',
		value: alarm.triggerCondition,
	},
	{
		label: 'Notes',
		value: alarm.notes,
	},
];

const getFlexidasFields = (alarm: IActiveAlarmsFlexidas): TAlarmFieldInfo[] => [
	{
		label: 'Alarm ID',
		value: alarm.alarmId,
	},
	{
		label: 'Alarm Name',
		value: alarm.alarmName,
	},
	{
		label: 'Node',
		value: alarm.alarmNodeIndex,
	},
	{
		label: 'DAS Name',
		value: alarm.alarmDasIndex,
	},
	{
		label: 'Pathname',
		value: alarm.alarmPathName,
	},
	{
		label: 'Severity',
		value: alarm.alarmSeverity.title,
		color: alarm.alarmSeverity.color,
	},
	{
		label: 'Description',
		value: alarm.alarmMessage,
	},
	{
		label: 'Raised',
		value: alarm.alarmReceivedTimeAtText || '-',
		separator: true,
	},
	{
		label: 'Cleared time',
		value: alarm.alarmClearedTimeAtText || '-',
	},
];

const getOranFields = (alarm: IActiveAlarmOran, editNotes?: () => void): TAlarmFieldInfo[] => [
	{
		label: 'Alarm ID',
		value: alarm.alarmId.title,
		onClick: () => copyTextUtil(alarm.alarmId.title),
	},
	{
		label: 'Alarm Name',
		value: alarm.name,
		onClick: () => copyTextUtil(alarm.name),
	},
	{
		label: 'Group',
		value: alarm.groups.map((group) => group.title).join(', '),
	},
	{
		label: 'Status',
		value: alarm.status.title,
	},
	{
		label: 'Severity',
		value: alarm.alarmSeverity.title,
		color: alarm.alarmSeverity.color,
	},
	{
		label: 'Applicability',
		value: alarm.applicability.title,
	},
	{
		label: 'Module Name',
		value: alarm.moduleName.title,
	},
	{
		label: 'Network Element',
		value: alarm.networkElementName,
	},
	{
		label: 'Middleware',
		value: alarm.middlewareName,
	},
	{
		label: 'Probable Cause',
		value: alarm.probableCause,
	},
	{
		label: 'Raised',
		value: alarm.alarmTimeText || '-',
		separator: true,
	},
	{
		label: 'Duration',
		value: generateDurationString(alarm.durationMinutes),
	},
	{
		label: 'Acknowledge',
		value: alarm.isAcknowledged ? 'Yes' : 'No',
	},
	{
		label: 'Acknowledge Notes',
		value: alarm.acknowledgeNotes || '-',
		icon: Icons.editIcon,
		handler: editNotes,
	},
	{
		label: 'Acknowledged by',
		value: alarm.acknowledgedBy || '-',
	},
	{
		label: 'Updated',
		value: alarm.updatedAtText || '-',
		separator: true,
	},
	{
		label: 'Cleared',
		value: alarm.clearedAtText || '-',
	},
	{
		label: 'Cleared by',
		value: alarm.clearedBy || '-',
	},
];

const defaultDetails = {
	fields: [],
	isClear: false,
	isAcknowledge: false,
};

interface IVendorAlarm {
	vendor: string;
	editNotes?: () => void;
	alarm: any;
}

export const getAlarmDetails = ({ vendor, alarm, editNotes }: IVendorAlarm) => {
	if (!alarm) {
		return defaultDetails;
	}

	switch (vendor) {
		case EVendorsValues.oran:
			return {
				fields: getOranFields(alarm, editNotes),
				...getAlarmAvailableActions({ vendor, alarm }),
			};
		case EVendorsValues.limenet:
			return {
				fields: getLimenetFields(alarm),
				...getAlarmAvailableActions({ vendor, alarm }),
			};
		case EVendorsValues.flexidas:
			return {
				fields: getFlexidasFields(alarm),
				...getAlarmAvailableActions({ vendor, alarm }),
			};
		default: {
			return defaultDetails;
		}
	}
};

export const getAlarmAvailableActions = ({ vendor, alarm }: IVendorAlarm) => {
	switch (vendor) {
		case EVendorsValues.oran:
			return {
				isClear: alarm?.status?.name !== EAlarmStatus.cleared,
				isAcknowledge: !alarm?.isAcknowledged,
			};
		case EVendorsValues.limenet:
			return {
				isClear: alarm.status.name === EAlarmStatus.active,
				isAcknowledge: false,
			};
		default: {
			return {
				isClear: false,
				isAcknowledge: false,
			};
		}
	}
};
